import { memo, useEffect, useRef } from "react";

import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import useApplyFilter from "components/Filters/useApplyFilter";
import {
  CircleCrossed,
  CloudSettings,
  GitBranch,
  Space,
  StarFilled,
} from "components/icons/generated";
import ListEntitiesItem from "components/ListEntitiesItem";
import ListEntitiesItemDescription from "components/ListEntitiesItem/Description";
import ListEntitiesItemLink from "components/ListEntitiesItem/Link";
import MetaInfoList from "components/MetaInfoList";
import MetaInfoListItem from "components/MetaInfoList/Item";
import MetaInfoProjectRoot from "components/MetaInfoList/ProjectRoot";
import ModuleVersionStateBadge from "components/ModuleVersionStateBadge";
import { projectRootTooltipContent } from "components/TooltipSnippets";
import ViewCustomizationItem from "components/ViewCustomization/Item";
import { ModuleSuggestions, searchModuleSuggestionsDictionary } from "constants/module";
import { VCS_PROVIDERS_ICONS } from "constants/provider_icons";
import Box from "ds/components/Box";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Icon from "ds/components/Icon";
import Toggle from "ds/components/Toggle";
import Tooltip from "ds/components/Tooltip";
import useSetModuleFavorite from "shared/Module/useSetModuleFavorite";
import { Module, VersionState } from "types/generated";
import { generateBranchUrl, generateRepoUrlText } from "utils/urls";
import DisableButton from "views/Account/Module/Versions/Components/DisableButton";
import EnableButton from "views/Account/Module/Versions/Components/EnableButton";
import YankedBadge from "views/Account/Module/Versions/Components/YankedBadge";
import useCanEnableModule from "shared/Module/access/useCanEnableModule";
import useCanDisableModule from "shared/Module/access/useCanDisableModule";

import TagsListFilterable from "../../../../../components/TagsList/Filterable";
import { MODULE_LIST_VIEW_ITEM_ID } from "../../constants";
import styles from "./styles.module.css";
import { showModuleVersionInstructionsDrawer } from "../InstructionsDrawer";

const REFETCH_QUERIES = ["SearchModules", "SearchModulesSuggestions"];

export type ModuleListItemProps = {
  module: Module;
  setRowHeight?: (size: number) => void;
  onCheckItem: (moduleId: string, isSelected: boolean) => void;
  checked: boolean;
};

const ModuleListItem = (props: ModuleListItemProps) => {
  const { module, setRowHeight, onCheckItem, checked } = props;

  const { applySpaceFilter, applyFilter, applyLabelFilter } = useApplyFilter<ModuleSuggestions>({
    searchSuggestionsDictionary: searchModuleSuggestionsDictionary,
  });

  const rowRef = useRef<HTMLDivElement>(null);

  const moduleUrl = `/module/${module.id}`;

  const [setModuleFavorite, { loading: setFavoriteLoading }] = useSetModuleFavorite({
    refetchQueries: REFETCH_QUERIES,
  });

  const canEnableModule = useCanEnableModule()(module);
  const canDisableModule = useCanDisableModule()(module);

  const handleRowHeight = () => {
    if (setRowHeight && rowRef.current) {
      setRowHeight(rowRef.current.getBoundingClientRect().height);
    }
  };

  const handleCheckItem = (isSelected: boolean) => {
    onCheckItem(module.id, isSelected);
  };

  const handleStarModule = () => {
    setModuleFavorite({ id: module.id, star: !module.starred });
  };

  // on every render, update the row height
  useEffect(handleRowHeight);

  const shouldShowInstructions =
    !module.current?.yanked && module.current?.state === VersionState.Active;

  const handleOpenInstructions = () => {
    if (!module.current?.yanked && module.current?.state === VersionState.Active) {
      showModuleVersionInstructionsDrawer({
        moduleName: module.name,
        moduleId: module.id,
        versionNumber: module.current?.id,
        workflowTool: module.workflowTool,
        moduleSource: module.moduleSource,
      });
    }
  };

  return (
    <ListEntitiesItem
      ref={rowRef}
      className={styles.listItem}
      isActive={checked}
      direction="column"
      gap="small"
    >
      <Toggle
        variant="checkbox"
        id={module.id}
        onChange={handleCheckItem}
        isSelected={checked}
        className={styles.tickbox}
        aria-label={checked ? `Unselect ${module.name} module` : `Select ${module.name} module`}
      />
      <Box justify="between">
        <Box gap="medium" align="center">
          <ListEntitiesItemLink
            href={moduleUrl}
            title={module.name}
            titleColor={module.isDisabled ? "secondary" : "primary"}
          />

          {!!module.starred && <Icon src={StarFilled} color="warning" noShrink />}

          {module.isDisabled && (
            <Tooltip on={(props) => <Icon {...props} src={CircleCrossed} noShrink />}>
              Module is disabled
            </Tooltip>
          )}

          <ModuleVersionStateBadge state={module.current?.state} number={module.current?.number} />

          {module.current?.yanked && <YankedBadge notes={module.current.yankNote} />}
        </Box>

        <DropdownMenuEllipsis tooltip="Module actions">
          <CopyFieldDropdownMenuItem title="Copy ID" value={module.id} />
          <DisableButton
            canDisable={canDisableModule}
            moduleId={module.id}
            refetchQueries={REFETCH_QUERIES}
          />
          <EnableButton
            canEnable={canEnableModule}
            moduleId={module.id}
            refetchQueries={REFETCH_QUERIES}
          />
          <DropdownMenuItem href={`${moduleUrl}/settings`}>Settings</DropdownMenuItem>
          <DropdownMenuItem onAction={handleStarModule} loading={setFavoriteLoading}>
            {module.starred ? "Remove from favorites" : "Add to favorites"}
          </DropdownMenuItem>

          <DropdownMenuItem
            onAction={handleOpenInstructions}
            isDisabled={!shouldShowInstructions}
            tooltipPlacement="bottom"
            tooltip="There are no active versions for this module"
            isTooltipActive={!shouldShowInstructions}
          >
            Show instructions
          </DropdownMenuItem>
        </DropdownMenuEllipsis>
      </Box>

      <Box direction="column" gap="medium">
        <ViewCustomizationItem
          id={MODULE_LIST_VIEW_ITEM_ID.DESCRIPTION}
          onVisibilityChange={handleRowHeight}
        >
          {module.description && (
            <ListEntitiesItemDescription
              className={styles.moduleDescription}
              description={module.description}
              id={module.id}
            />
          )}
        </ViewCustomizationItem>

        <MetaInfoList>
          <ViewCustomizationItem
            id={MODULE_LIST_VIEW_ITEM_ID.SPACE}
            onVisibilityChange={handleRowHeight}
          >
            {module.spaceDetails && (
              <MetaInfoListItem
                applyFilter={() =>
                  applySpaceFilter(ModuleSuggestions.Space)(module.spaceDetails.id)
                }
                icon={Space}
                linkText={module.spaceDetails.name}
                href={`/spaces/${module.spaceDetails.id}`}
                type="space"
              />
            )}
          </ViewCustomizationItem>

          <ViewCustomizationItem
            id={MODULE_LIST_VIEW_ITEM_ID.PROVIDER}
            onVisibilityChange={handleRowHeight}
          >
            <MetaInfoListItem
              tooltip="Module provider"
              icon={CloudSettings}
              applyFilter={() =>
                applyFilter(ModuleSuggestions.ModuleProvider)(module.terraformProvider)
              }
            >
              {module.terraformProvider}
            </MetaInfoListItem>
          </ViewCustomizationItem>

          <ViewCustomizationItem
            id={MODULE_LIST_VIEW_ITEM_ID.REPOSITORY}
            onVisibilityChange={handleRowHeight}
          >
            {module.apiHost && (
              <MetaInfoListItem
                applyFilter={applyFilter(ModuleSuggestions.Repository)}
                linkText={generateRepoUrlText({
                  namespace: module.namespace,
                  repository: module.repository,
                })}
                href={generateBranchUrl({
                  apiHost: module.apiHost,
                  branch: module.branch,
                  namespace: module.namespace,
                  repository: module.repository,
                  repositoryURL: module.repositoryURL,
                  provider: module.provider,
                  projectRoot: module.projectRoot,
                })}
                icon={VCS_PROVIDERS_ICONS[module.provider]}
                target="_blank"
                type="repository"
              />
            )}
          </ViewCustomizationItem>

          <ViewCustomizationItem
            id={MODULE_LIST_VIEW_ITEM_ID.PROJECT_ROOT}
            onVisibilityChange={handleRowHeight}
          >
            {module.projectRoot && (
              <MetaInfoProjectRoot
                projectRoot={module.projectRoot}
                tooltip={projectRootTooltipContent}
                handleApplyFilter={applyFilter(ModuleSuggestions.ProjectRoot)}
              />
            )}
          </ViewCustomizationItem>

          <ViewCustomizationItem
            id={MODULE_LIST_VIEW_ITEM_ID.BRANCH}
            onVisibilityChange={handleRowHeight}
          >
            {module.apiHost && (
              <MetaInfoListItem
                applyFilter={applyFilter(ModuleSuggestions.Branch)}
                linkText={module.branch}
                href={generateBranchUrl({
                  apiHost: module.apiHost,
                  namespace: module.namespace,
                  repository: module.repository,
                  repositoryURL: module.repositoryURL,
                  provider: module.provider,
                  branch: module.branch,
                })}
                icon={GitBranch}
                target="_blank"
                type="branch"
              />
            )}
          </ViewCustomizationItem>

          <ViewCustomizationItem
            id={MODULE_LIST_VIEW_ITEM_ID.ADMINISTRATIVE}
            onVisibilityChange={handleRowHeight}
          >
            {module.administrative && (
              <MetaInfoListItem
                applyFilter={() => applyFilter(ModuleSuggestions.Administrative)("true")}
              >
                Administrative
              </MetaInfoListItem>
            )}
          </ViewCustomizationItem>
        </MetaInfoList>
      </Box>

      <ViewCustomizationItem
        id={MODULE_LIST_VIEW_ITEM_ID.TAGS}
        onVisibilityChange={handleRowHeight}
      >
        <TagsListFilterable
          tags={module.labels}
          applyLabelFilter={applyLabelFilter(ModuleSuggestions.Label)}
          applyFolderFilter={applyFilter(ModuleSuggestions.Folder)}
          onExpand={handleRowHeight}
        />
      </ViewCustomizationItem>
    </ListEntitiesItem>
  );
};

export default memo(ModuleListItem);
